import classNames from "classnames";
import { Entry } from "contentful";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";

import { useWindowSize } from "~/contexts/breakpoint";
import { useGiftCardMedia } from "~/contexts/giftcard-media";
import CrossCountry from "~/types/cross-country";
import Editorials from "~/types/editorials";
import { basicAnimationStates } from "~/utils/animation-utils";
import imageUtils from "~/utils/image-utils";
import productUtils from "~/utils/product-utils";
import TrackingUtils from "~/utils/tracking-utils";

import EscapeNewLine from "../common/escape-new-line";
import styles from "./product-shade-selector.module.scss";

function isVirtualGiftCard(
  el: CrossCountry.Product | Entry<Editorials.VirtualCard>
): el is Entry<Editorials.VirtualCard> {
  return "sys" in el;
}

type ShadeSelectorProps = {
  open: boolean;
  onClose: () => void;
  shades: CrossCountry.Product[] | Entry<Editorials.VirtualCard>[] | undefined;
  selectedShade: CrossCountry.ChildrenProduct;
  root: CrossCountry.Product;
  minHeight?: number;
  setShadeSelectorHeight?: Dispatch<SetStateAction<number>>;
  className?: string;
  shadeIsClicked(currentState: boolean): void;
};

export function ShadeSelectorWrapper(props: ShadeSelectorProps) {
  const shadesRef = useRef<HTMLDivElement>(null);
  const { selectedIndex, setSelectedIndex } = useGiftCardMedia();

  const [minHeight, setMinHeight] = useState(props.minHeight ?? 0);

  useEffect(() => {
    if (shadesRef.current && !!props.minHeight) {
      let firstChild = shadesRef.current.children[0];
      if (firstChild) {
        setMinHeight(firstChild.scrollHeight);
      }
    }
  }, [props.minHeight, props.selectedShade]);

  useEffect(() => {
    props.setShadeSelectorHeight?.(props.open ? shadesRef.current?.offsetHeight ?? 0 : minHeight);
  }, [minHeight, props]);

  return (
    <div
      className={styles.wrapperShades}
      style={{ height: props.open ? shadesRef.current?.offsetHeight : minHeight || 0 }}
    >
      <div ref={shadesRef} className={classNames(styles.shadesSelector, props.className)}>
        {props.shades?.map((shade, i) => {
          // giftcard custom selectro
          if (isVirtualGiftCard(shade)) {
            const currentPreviewUrl = shade.fields.shadePreview.fields
              ? "https:" + shade.fields.shadePreview.fields.file.url
              : undefined;
            return (
              <div key={shade.sys.id}>
                <button
                  role="link"
                  aria-label={shade.fields.cardImage.fields.title}
                  className={i === selectedIndex ? styles.selectedShadeSelectorButton : styles.shadeSelectorButton}
                  onClick={() => {
                    setSelectedIndex(i);
                  }}
                >
                  <div className={styles.shadeImageWrapper}>
                    <Image
                      className={styles.shadeImage}
                      alt={props.selectedShade.display_value || ""}
                      src={currentPreviewUrl ?? ""}
                      height={20}
                      width={20}
                      loader={imageUtils.contentfulImageLoader}
                      aria-hidden
                    />
                  </div>
                </button>
                <div className={styles.shadeSelectorCode}>
                  <p>{i + 1}</p>
                </div>
              </div>
            );
          }
          return (
            <Link
              key={shade.product_id}
              role="radio"
              scroll={false}
              shallow
              aria-label={shade.display_value || shade.color}
              aria-checked={shade.product_id === props.selectedShade?.product_id}
              aria-disabled={!shade.has_stock}
              href={`/p/${shade.slug ?? props.root.slug}-${shade.product_id}`}
              className={
                shade.product_id === props.selectedShade?.product_id
                  ? styles.selectedShadeSelectorButton
                  : styles.shadeSelectorButton
              }
              onClick={() => {
                TrackingUtils.track(
                  TrackingUtils.mapEcommerceEvent("view_item", [TrackingUtils.mapEcomItem(props.root, shade)], {
                    user_action: "detail_change_color",
                  })
                );
                props.shadeIsClicked(props.open);
              }}
            >
              <div className={styles.shadeImageWrapper}>
                {!shade.has_stock && <span className={styles.outOfStock} />}
                <Image
                  className={styles.shadeImage}
                  alt={shade.display_value || shade.color || ""}
                  src={productUtils.getProductDroplet(shade?.product_media?.media)?.url || imageUtils.transparentPixel}
                  height={20}
                  width={20}
                  loader={imageUtils.evaImageLoader}
                />
              </div>
              <div className={styles.shadeSelectorCode} aria-hidden>
                <p>{shade.color}</p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

type ProductShadeProps = {
  root: CrossCountry.Product;
  shades: CrossCountry.Product[] | Entry<Editorials.VirtualCard>[];
  selectedShade: CrossCountry.ChildrenProduct;
};

export default function ProductShadeSelector(props: ProductShadeProps) {
  const t = useTranslations();

  const windowSize = useWindowSize();
  const { selectedIndex, mediaUrl: mediaGiftCardUrl } = useGiftCardMedia();

  const [shadesExpanded, setShadesExpanded] = useState(false);

  const currentShadeName = (() => {
    if (props.selectedShade) {
      return props.selectedShade.color || props.selectedShade.color_variant;
    }

    const indexedShade = props.shades[selectedIndex];

    if (indexedShade && isVirtualGiftCard(indexedShade)) {
      return <EscapeNewLine text={indexedShade.fields.name} />;
    }
  })();

  const currentShadesPerBrekpoint = useMemo(() => {
    if (!windowSize.width || windowSize.width >= 1440) {
      return 8;
    } else if (windowSize.width >= 1280) {
      return 7;
    } else if (windowSize.width >= 900) {
      return 5;
    } else if (windowSize.width >= 640) {
      return 12;
    } else {
      return 8;
    }
  }, [windowSize]);

  function shadeIsClicked(currentState: boolean): void {
    setShadesExpanded(currentState);
  }

  return (
    <>
      <div className={styles.shadesHeader}>
        <div className={styles.colorName}>{currentShadeName}</div>
        {props.shades && props.shades.length > currentShadesPerBrekpoint && (
          <AnimatePresence mode="wait">
            <motion.button
              key={`shades-toggle-${shadesExpanded}`}
              initial={basicAnimationStates.off}
              animate={basicAnimationStates.on}
              exit={basicAnimationStates.off}
              transition={{ duration: 0.3 }}
              className={styles.shadesCount}
              onClick={() => setShadesExpanded(!shadesExpanded)}
            >
              {shadesExpanded ? t("generic.showless") : t("generic.shadescount", { count: props.shades?.length })}
            </motion.button>
          </AnimatePresence>
        )}
      </div>
      <ShadeSelectorWrapper
        minHeight={mediaGiftCardUrl ? 41 : 74}
        root={props.root}
        selectedShade={props.selectedShade}
        shades={props.shades}
        open={shadesExpanded}
        onClose={() => setShadesExpanded(false)}
        shadeIsClicked={shadeIsClicked}
      />
    </>
  );
}
